import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash'; // lodash debounce for better performance
import { FormControl, FormHelperText } from '@mui/material';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from 'screens/components/basic/Button';
import { getProducts, auditProducts } from 'actions/EntityActions';
import { formInput, clearFormInput } from 'actions/FormActions';

const AutoCompleteInput = ({ name, label, hintText, methodName, productType,  minLength = 3, required, className }) => {
  const dispatch = useDispatch();

  // Getting state from Redux store
  const { branchId } = useSelector((state) => state.common) || [];
  const { productList, auditProductList } = useSelector((state) => state.entity) || [];
  const { [methodName]: searchMethod, error: formError } = useSelector((state) => state.form) || "";  // dynamic search method name
  const { [name]: reduxError } = formError || "";
  
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Define Product options
    if (!productType && productList && productList.length > 0) {
      // console.log(productList);
      const newOptions = productList.map((item) => ({
        title: item.name,
        name: searchMethod === 'sku' ? item.sku.toString() : item.name,
        categoryId: item.categoryId,
        id: item.id.toString(),
        value: item.id.toString(),
      }));
      setOptions(newOptions); 
    }
  }, [productType, productList]);

  useEffect(() => {
    // Define Product options
    if (productType === 'audit' && auditProductList && auditProductList.length > 0) {
      // console.log(productList);
      const newOptions = auditProductList.map((item) => ({
        title: item.name,
        name: searchMethod === 'sku' ? item.sku.toString() : item.name,
        categoryId: item.categoryId,
        id: item.id.toString(),
        value: item.id.toString(),
      }));
      setOptions(newOptions); 
    }
  }, [productType, auditProductList]);

  useEffect(() => {
    console.log(value, error, reduxError)
    if (!value && reduxError){
      setError(reduxError);
    }else{
      setError("");
    }
  }, [reduxError, value])

  const onSelect = (event, chosenRequest) => {
    if (chosenRequest){
      updateStore(chosenRequest.id || chosenRequest.value, chosenRequest.text, chosenRequest.categoryId || null);
      setError("")
    }else{
      setError(reduxError);
    }
  }

  const updateStore = (value, text, categoryId) => {
    const newValue = options.find(item => item.value === value);
    setValue(newValue);
    dispatch(formInput(value, name));
    dispatch(formInput(text, name + "Text"));

    if (categoryId) {
      dispatch(formInput(categoryId, name + "CategoryId"));
    }
  }

  const searchProducts = () => {
    // console.log("searchText: " + searchText);
    if (!searchText) {
      alert('Please input search text!');
      return;
    } else if (searchText.length >=3){
      const model = {
        page: 1,
        itemsPerPage: 9999,
        searchText,
        categoryId: null,
        sku: searchMethod === 'sku' ? searchText : "",
        priceMatrixId: null,
        status: 'Active',
        orderBy: "ASC",
        sortBy: "Name",
        typeId: null,
        branchId,
        searchBySKU: searchMethod === 'sku' ? true : false
      };
      // console.log(model)
      dispatch(getProducts(model));
      setOpen(true);
    }
  }

  const searchAuditProducts = () => {
    if (!searchText) {
      alert('Please input search text!');
      return;
    } else if (searchText.length >=3){
      const model = {
        searchText,
        branchId,
        searchBySKU: searchMethod === 'sku' ? true : false
      };
      // console.log(model);
      dispatch(auditProducts(model));
      setOpen(true);
    }
  }

  const handleInputChange = (event, newInputValue) => {
    if (!newInputValue) { // need to aware when the component is loaded, the value is undefined
      setSearchText('');
      dispatch(clearFormInput(name));
      dispatch(clearFormInput(`${name}Text`));
    } else if (newInputValue.length < minLength && newInputValue.length > 0){
      setError(`The minimum length of this field is ${minLength}!`)
      setSearchText(newInputValue);
      dispatch(formInput(value, name));
    }else{
      setError('')
      setSearchText(newInputValue);
      dispatch(formInput(value, name));
    }
  };

  return (
    <div className="form-group ProductSearchSelect">
      <FormControl className={`${className} ${Boolean(error) || !value || value.length === 0 ? 'error' : ``}`} error={Boolean(error)} fullWidth>
        <Autocomplete
          open={open}
          onClose={() => setOpen(false)}
          freeSolo
          fullWidth
          id={name}
          value={value}
          filter={Autocomplete.caseInsensitiveFilter}
          options={options}
          getOptionLabel={options => options.name}
          onChange={onSelect}
          onInputChange={handleInputChange}
          InputLabelProps={{ shrink: true }} 
          openOnFocus={open}
          listStyle={{ maxHeight: 200, overflow: 'auto', width: 'fit-content' }}
          renderInput={(params) => (
            <TextField
              {...params}
              InputLabelProps={{ shrink: true }}
              label={label}
              variant="standard"
              placeholder={`${hintText}, please input at least 3 characters`}
              required={required}
              min={3}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <span>Loading...</span> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              onClick={() => setOpen(prevOpen => !prevOpen)}
              error={error}
              helperText={error || ``}
            />
          )}
        />
      </FormControl>
      <Button
          type="IconSearchButtonLabel"
          className="SearchBtn btnNewBrandPOS"
          id="searchProductButton"
          mini={true}
          label="Search Product"
          onTouchTap={() => {productType === "audit" ? searchAuditProducts() : searchProducts()}}
        />
    </div>
  );
}

export default AutoCompleteInput;